/**
 * Socket.IO Service - Singleton para conexão WebSocket
 *
 * Gerencia conexão persistente com o servidor para comunicação em tempo real
 * Usado principalmente pelo PlotCalc para receber progresso de cálculos
 */

import { io, Socket } from "socket.io-client";
import {
	API_URL,
	getRuntimeSocketServerUrl,
	SOCKET_CONFIGURATION_ERROR,
	USE_PROXY,
} from "../config";
import { TokenService } from "./tokenService";
import { sendToTerminal } from "./terminalLogger";
import { getTenantIdForWebSocket } from "../utils/getTenantId";

// ==================== TYPES ====================

export interface PlotCalcProgressEvent {
	requestId: string;
	percent: number;
	status: string;
}

export interface PlotCalcConnectionInfo {
	route: "proxy" | "direct" | "direct-fallback";
	useProxy: boolean;
	connectHost: string;
	connectPort: number;
	proxyHost?: string;
	proxyPort?: number;
	fallbackReason?: string;
	proxyHealthStatus?: "unknown" | "ready" | "down";
	proxyHealthCheckedAt?: string | null;
}

export interface PlotCalcResultEvent {
	requestId: string;
	success: boolean;
	maximumY_mm?: number;
	// Array flat de figuras (compatibilidade)
	figures?: Array<{
		curveIndex: number;
		x_mm: number;
		y_mm: number;
		rotation_degrees: number;
		materialIndex?: number;
		lineIndex?: number;
		sheetIndex?: number;
		localX_mm?: number;
		localY_mm?: number;
		sheetOffsetX_mm?: number;
		sheetOffsetY_mm?: number;
	}>;
	// ✅ Estrutura hierárquica: materials[] → lines[] → sheets[] → figures[]
	materials?: Array<{
		materialIndex: number;
		lines: Array<{
			height_mm: number;
			sheets: Array<{
				width_mm: number;
				figures: Array<{
					curveIndex: number;
					x_mm: number;
					y_mm: number;
					rotation_degrees: number;
					materialIndex?: number;
					lineIndex?: number;
					sheetIndex?: number;
					localX_mm?: number;
					localY_mm?: number;
					sheetOffsetX_mm?: number;
					sheetOffsetY_mm?: number;
				}>;
			}>;
		}>;
	}>;
	errors?: boolean;
	sessionId?: number;
	connection?: PlotCalcConnectionInfo;
	meta?: {
		duration: number;
		figuresCount: number;
		materialsCount?: number;
		emittedAtMs?: number;
		serial: string;
	};
}

export interface PlotCalcErrorEvent {
	requestId: string;
	code: string;
	message: string;
	connection?: PlotCalcConnectionInfo;
}

export interface PlotCalcStartedEvent {
	requestId: string;
	status: string;
}

export interface PlotCalcSessionEvent {
	requestId: string;
	sessionId: number;
}

export interface SocketTraceContext {
	source?: string;
	reason?: string;
}

type SocketTracePayloadInput = {
	source: string;
	action: string;
	connectionSeq: number | null;
	reason?: string;
	tenantId?: string | null;
	connected?: boolean;
	socketId?: string | null;
	[key: string]: unknown;
};

const SOCKET_TRACE_ENABLED = import.meta.env.DEV;
const DEFAULT_SOCKET_TRACE_SOURCE = "unspecified";

function createSocketTracePayload(
	input: SocketTracePayloadInput,
): SocketTracePayloadInput & { ts: string } {
	return {
		ts: new Date().toISOString(),
		...input,
	};
}

export const __socketDiagnostics = {
	createTracePayload: createSocketTracePayload,
};

// ==================== SINGLETON ====================

/** Resultado de ensureConnected — caller decide o que fazer em falha. */
export type EnsureConnectedResult =
	| { ok: true }
	| { ok: false; reason: "timeout" | "auth_failed" | "no_socket" | "error"; message?: string };

class SocketService {
	private socket: Socket | null = null;
	private isConnecting = false;
	private reconnectAttempts = 0;
	private connectionSeqCounter = 0;
	private currentConnectionSeq: number | null = null;
	private configurationError: string | null = SOCKET_CONFIGURATION_ERROR;
	/** Marca se a última falha foi de auth — bloqueia retries até auth:updated. */
	private authFailed = false;
	/** Listeners do window (online/offline) registrados uma única vez. */
	private windowListenersAttached = false;
	/** Conjunto de subscribers para mudanças de estado (useSyncExternalStore). */
	private stateSubscribers = new Set<() => void>();

	/**
	 * Obtém o token JWT atual para autenticação do handshake.
	 */
	private getAuthToken(): string | undefined {
		const token = TokenService.getAccessToken();
		return token || undefined;
	}

	/**
	 * Monta payload de auth para socket.io (handshake.auth).
	 */
	private getSocketAuthPayload(): Record<string, string> {
		const token = this.getAuthToken();
		return token ? { token } : {};
	}

	/**
	 * Reaplica credenciais atuais no socket já existente.
	 */
	private applyLatestAuthToSocket(): void {
		if (!this.socket) return;
		this.socket.auth = this.getSocketAuthPayload();
	}

	private safeGetTenantId(): string | null {
		try {
			return getTenantIdForWebSocket();
		} catch {
			return null;
		}
	}

	private trace(
		action: string,
		trace: SocketTraceContext = {},
		extra: Record<string, unknown> = {},
	): void {
		if (!SOCKET_TRACE_ENABLED) {
			return;
		}

		const payload = createSocketTracePayload({
			source: trace.source || DEFAULT_SOCKET_TRACE_SOURCE,
			action,
			connectionSeq:
				typeof extra.connectionSeq === "number"
					? (extra.connectionSeq as number)
					: this.currentConnectionSeq,
			reason:
				trace.reason ||
				(typeof extra.reason === "string"
					? (extra.reason as string)
					: undefined),
			tenantId:
				typeof extra.tenantId === "string" || extra.tenantId === null
					? (extra.tenantId as string | null)
					: this.safeGetTenantId(),
			connected:
				typeof extra.connected === "boolean"
					? (extra.connected as boolean)
					: this.socket?.connected ?? false,
			socketId:
				typeof extra.socketId === "string"
					? (extra.socketId as string)
					: this.socket?.id || "pending",
			...extra,
		});

		sendToTerminal(`🧭 [SOCKET_TRACE] ${action}`, payload);
	}

	private startHandshake(
		action: string,
		trace: SocketTraceContext = {},
		extra: Record<string, unknown> = {},
	): number {
		const connectionSeq = ++this.connectionSeqCounter;
		this.currentConnectionSeq = connectionSeq;
		this.trace(action, trace, {
			...extra,
			connectionSeq,
		});
		return connectionSeq;
	}

	/**
	 * Obtém ou cria a conexão WebSocket
	 */
	private setConfigurationError(message: string): void {
		if (this.configurationError === message) {
			return;
		}

		this.configurationError = message;
		sendToTerminal("❌ [SOCKET] Configuração inválida", { message });
		this.notifyStateChange();
	}

	private clearConfigurationError(): void {
		if (!this.configurationError) {
			return;
		}

		this.configurationError = null;
		this.notifyStateChange();
	}

	getConfigurationError(): string | null {
		return this.configurationError;
	}

	getSocket(trace: SocketTraceContext = {}): Socket | null {
		this.trace("getSocket", trace, {
			hasSocket: !!this.socket,
		});

		if (this.socket && this.socket.connected) {
			this.trace("getSocket.hit_connected", trace, {
				hasSocket: true,
			});
			return this.socket;
		}

		if (!this.socket) {
			this.trace("getSocket.create_missing_socket", trace, {
				hasSocket: false,
			});
			this.connect({
				source: trace.source,
				reason: trace.reason || "getSocket:create",
			});
		}

		return this.socket;
	}

	/**
	 * Conecta ao servidor WebSocket
	 */
	connect(trace: SocketTraceContext = {}): Socket | null {
		if (this.socket) {
			this.applyLatestAuthToSocket();
			if (!this.socket.connected) {
				this.startHandshake("connect.reuse_existing_socket", trace, {
					hasSocket: true,
				});
				this.socket.connect();
			}
			sendToTerminal("🔌 [SOCKET] Socket já existe", {
				connected: this.socket.connected,
				id: this.socket.id,
			});
			this.trace("connect.reuse_existing_socket.done", trace, {
				hasSocket: true,
			});
			return this.socket;
		}

		if (this.isConnecting) {
			sendToTerminal("🔌 [SOCKET] Conexão já em andamento, aguardando...");
			this.trace("connect.skipped_already_connecting", trace, {
				hasSocket: false,
			});
			return this.socket;
		}

		this.isConnecting = true;
		const socketServerUrl = getRuntimeSocketServerUrl();

		if (!socketServerUrl) {
			this.isConnecting = false;
			this.setConfigurationError(
				SOCKET_CONFIGURATION_ERROR ||
					"Configuração insuficiente para iniciar o WebSocket.",
			);
			return null;
		}

		this.clearConfigurationError();

		// Obter tenantId para identificação multi-tenant
		const tenantId = getTenantIdForWebSocket();
		const authPayload = this.getSocketAuthPayload();
		const hasToken = !!authPayload.token;
		const connectionSeq = this.startHandshake("connect.create_socket", trace, {
			tenantId,
			hasToken,
			hasSocket: false,
		});

		if (!hasToken) {
			sendToTerminal("⚠️ [SOCKET] Conexão iniciada sem token JWT no handshake");
		}

		sendToTerminal(
			"═══════════════════════════════════════════════════════════",
		);
		sendToTerminal("🔌 [SOCKET] INICIANDO CONEXÃO WEBSOCKET", {
			url: API_URL,
			tenantId,
			transports: ["websocket", "polling"],
			timeout: 20000,
			reconnectionAttempts: "Infinity",
		});

		try {
			const proxyEnabled = USE_PROXY;
			this.socket = io(socketServerUrl, {
				query: {
					tenantId,
				},
				auth: authPayload,
				transports: ["websocket"], //["websocket", "polling"],
				path: "/socket.io",
				autoConnect: true,
				reconnection: true,
				// ✅ Tentar para sempre — perdas transitórias (Wi-Fi, sleep, picos)
				// não devem deixar o socket "morto" como acontecia com 5 tentativas.
				reconnectionAttempts: Infinity,
				reconnectionDelay: 1000,
				reconnectionDelayMax: 5000,
				// ✅ Jitter evita thundering herd quando vários clientes reconectam juntos.
				randomizationFactor: 0.5,
				timeout: 20000,
				withCredentials: true, // Necessário para CORS com credentials
			});

			sendToTerminal("🔌 [SOCKET] Socket.IO instanciado", {
				hasSocket: !!this.socket,
				socketId: this.socket?.id || "pending",
				socketServerUrl,
				proxyEnabled,
				tenantId,
				hasToken,
			});
			this.trace("connect.socket_instantiated", trace, {
				connectionSeq,
				socketServerUrl,
				proxyEnabled,
				tenantId,
				hasToken,
				hasSocket: !!this.socket,
			});

			this.setupEventHandlers();
		} catch (error) {
			this.socket = null;
			sendToTerminal("💥 [SOCKET] ERRO ao criar socket", {
				error: error instanceof Error ? error.message : String(error),
				stack:
					error instanceof Error
						? error.stack?.split("\n").slice(0, 2).join("\n")
						: undefined,
			});
			this.trace("connect.create_socket.error", trace, {
				connectionSeq,
				error: error instanceof Error ? error.message : String(error),
			});
		}

		this.isConnecting = false;
		return this.socket;
	}

	/**
	 * Configura handlers de eventos do socket.
	 *
	 * ✅ Best practices socket.io v4 (https://socket.io/docs/v4/client-socket-instance):
	 * - `socket.active` indica se a reconexão automática vai disparar.
	 *   Quando `active === false`, o caller PRECISA chamar `socket.connect()` manualmente.
	 * - Razões com auto-reconnect: ping timeout, transport close, transport error.
	 * - Razões sem auto-reconnect: io server disconnect, io client disconnect.
	 */
	private setupEventHandlers(): void {
		if (!this.socket) return;

		this.socket.io.on("reconnect_attempt", (attemptNumber) => {
			this.startHandshake(
				"manager.reconnect_attempt",
				{
					source: "socket.io-manager",
					reason: `attempt:${attemptNumber}`,
				},
				{ attemptNumber },
			);
		});

		this.socket.on("connect", () => {
			sendToTerminal("✅ [SOCKET] Conectado ao WebSocket", {
				id: this.socket?.id,
				transport: this.socket?.io?.engine?.transport?.name,
			});
			this.trace("event.connect", {
				source: "socket.io-event",
				reason: "connect",
			});
			this.reconnectAttempts = 0;
			this.authFailed = false;
			this.notifyStateChange();

			// Re-entrar na sala do usuário após conectar
			this.joinUserRoom();
		});

		this.socket.on("disconnect", (reason) => {
			const willAutoReconnect = this.socket?.active ?? false;
			sendToTerminal("❌ [SOCKET] Desconectado", {
				reason,
				willAutoReconnect,
			});
			this.trace(
				"event.disconnect",
				{
					source: "socket.io-event",
					reason,
				},
				{ willAutoReconnect },
			);
			this.notifyStateChange();

			// Disconnects "permanentes" exigem connect() manual.
			// Não fazemos auto-retry aqui se foi `io client disconnect` (intencional)
			// nem se foi `io server disconnect` por logout/auth.
			if (!willAutoReconnect && reason === "io server disconnect") {
				sendToTerminal(
					"⚠️ [SOCKET] Disconnect server-side — aguardando ação explícita (login/auth:updated)",
				);
			}
		});

		this.socket.on("connect_error", (error) => {
			this.reconnectAttempts++;
			const errorType = this.classifyError(error);
			const willAutoReconnect = this.socket?.active ?? false;
			const isAuthError = errorType === "UNAUTHORIZED";

			if (isAuthError) {
				this.authFailed = true;
			}

			sendToTerminal("⚠️ [SOCKET] Erro de conexão", {
				errorMessage: error.message,
				errorType,
				errorName: error.name,
				attempt: this.reconnectAttempts,
				willAutoReconnect,
				url: API_URL,
				cause: (error as any).cause?.message || null,
				description: (error as any).description || null,
			});
			this.trace(
				"event.connect_error",
				{
					source: "socket.io-event",
					reason: errorType,
				},
				{
					attempt: this.reconnectAttempts,
					willAutoReconnect,
					errorMessage: error.message,
				},
			);
			this.notifyStateChange();
		});

		this.socket.on("reconnect", (attemptNumber) => {
			sendToTerminal("🔄 [SOCKET] Reconectado", { attemptNumber });
			this.trace(
				"event.reconnect",
				{
					source: "socket.io-event",
					reason: `attempt:${attemptNumber}`,
				},
				{ attemptNumber },
			);
			this.notifyStateChange();
			this.joinUserRoom();
		});

		this.socket.on("reconnect_error", (error) => {
			sendToTerminal("❌ [SOCKET] Falha em tentativa de reconexão", {
				error: error.message,
			});
			this.trace(
				"event.reconnect_error",
				{
					source: "socket.io-event",
					reason: "reconnect_error",
				},
				{ error: error.message },
			);
		});

		// reconnect_failed só dispara se reconnectionAttempts não for Infinity.
		// Mantemos por segurança, mas com Infinity não deve acontecer.
		this.socket.on("reconnect_failed", () => {
			sendToTerminal(
				"❌ [SOCKET] reconnect_failed disparado — forçando connect() manual",
			);
			this.trace("event.reconnect_failed", {
				source: "socket.io-event",
				reason: "reconnect_failed",
			});
			this.startHandshake("event.reconnect_failed.manual_connect", {
				source: "socket.io-event",
				reason: "reconnect_failed",
			});
			this.socket?.connect();
		});

		this.attachWindowListeners();
	}

	/**
	 * Listeners de `online`/`offline` do navegador.
	 * Quando a rede volta (Wi-Fi, sleep do Windows, troca de rede),
	 * forçamos um connect() para acelerar a reconexão (sem esperar o backoff).
	 */
	private attachWindowListeners(): void {
		if (this.windowListenersAttached) return;
		if (typeof window === "undefined") return;

		const onOnline = () => {
			if (!this.socket) return;
			if (this.socket.connected) return;
			// ✅ CORREÇÃO: Se authFailed mas agora temos token válido, resetar e tentar
			if (this.authFailed) {
				const token = this.getAuthToken();
				if (token) {
					this.authFailed = false;
					this.applyLatestAuthToSocket();
					sendToTerminal(
						"🌐 [SOCKET] online detectado — token recuperado, resetando auth_failed",
					);
				} else {
					sendToTerminal(
						"🌐 [SOCKET] online detectado, mas auth_failed — aguardando auth:updated",
					);
					return;
				}
			}
			sendToTerminal("🌐 [SOCKET] window.online — forçando reconexão");
			this.startHandshake("window.online.manual_connect", {
				source: "window-online",
				reason: "browser-online",
			});
			this.socket.connect();
		};

		const onOffline = () => {
			sendToTerminal("📴 [SOCKET] window.offline — pausando até voltar a rede");
		};

		window.addEventListener("online", onOnline);
		window.addEventListener("offline", onOffline);
		this.windowListenersAttached = true;
	}

	/**
	 * Notifica subscribers de useSyncExternalStore que o estado mudou.
	 */
	private notifyStateChange(): void {
		this.stateSubscribers.forEach((cb) => {
			try {
				cb();
			} catch {
				/* ignore subscriber errors */
			}
		});
	}

	/**
	 * Subscreve em mudanças de estado (compatível com useSyncExternalStore).
	 * Retorna função de unsubscribe.
	 */
	subscribe(callback: () => void): () => void {
		this.stateSubscribers.add(callback);
		return () => {
			this.stateSubscribers.delete(callback);
		};
	}

	/**
	 * Snapshot booleano para useSyncExternalStore.
	 * IMPORTANTE: precisa retornar valor estável entre renders quando não muda.
	 */
	getConnectedSnapshot = (): boolean => {
		return this.socket?.connected ?? false;
	};

	/**
	 * Garante que o socket está conectado antes de uma operação crítica (ex.: emit).
	 *
	 * Comportamento:
	 * - Se já conectado, resolve imediatamente (`{ ok: true }`).
	 * - Se socket nulo (foi destruído), recria via `connect()`.
	 * - Se socket existe mas está desconectado, tenta reanimar com `socket.connect()`.
	 * - Aguarda evento `connect` com timeout (sem polling).
	 *
	 * @param timeoutMs tempo máximo de espera. Default 10s.
	 */
	async ensureConnected(timeoutMs = 10000): Promise<EnsureConnectedResult> {
		if (this.configurationError) {
			return {
				ok: false,
				reason: "error",
				message: this.configurationError,
			};
		}

		if (this.isConnected()) {
			return { ok: true };
		}

		if (this.authFailed) {
			return {
				ok: false,
				reason: "auth_failed",
				message: "Aguardando auth:updated após falha de autenticação",
			};
		}

		// Recria/reanima
		if (!this.socket) {
			this.connect({
				source: "ensure-connected",
				reason: "missing-socket",
			});
		} else if (!this.socket.connected) {
			this.applyLatestAuthToSocket();
			this.startHandshake("ensureConnected.socket_connect", {
				source: "ensure-connected",
				reason: "disconnected-socket",
			});
			this.socket.connect();
		}

		if (!this.socket) {
			return { ok: false, reason: "no_socket", message: "Falha ao instanciar socket" };
		}

		const socket = this.socket;

		return new Promise<EnsureConnectedResult>((resolve) => {
			let settled = false;

			const cleanup = () => {
				socket.off("connect", onConnect);
				socket.off("connect_error", onError);
				if (timer) clearTimeout(timer);
			};

			const onConnect = () => {
				if (settled) return;
				settled = true;
				cleanup();
				resolve({ ok: true });
			};

			const onError = (err: Error) => {
				// Erros transitórios continuam sendo tentados pelo socket.io.
				// Só falhamos cedo em auth (caller decide o que fazer).
				if (this.classifyError(err) === "UNAUTHORIZED") {
					if (settled) return;
					settled = true;
					cleanup();
					resolve({ ok: false, reason: "auth_failed", message: err.message });
				}
			};

			const timer = setTimeout(() => {
				if (settled) return;
				settled = true;
				cleanup();
				resolve({
					ok: false,
					reason: "timeout",
					message: `Não conectou em ${timeoutMs}ms`,
				});
			}, timeoutMs);

			socket.once("connect", onConnect);
			socket.on("connect_error", onError);
		});
	}

	/**
	 * Entra na sala do usuário (necessário para receber eventos)
	 * Envia tenantId como fallback caso não tenha sido identificado no handshake
	 */
	private joinUserRoom(): void {
		const userId = this.getUserId();
		if (!userId) {
			sendToTerminal("⚠️ [SOCKET] Sem userId para joinUser");
			return;
		}

		if (this.socket && this.socket.connected) {
			// Enviar tenantId também no joinUser como fallback
			// Backend aceita ambos os formatos: string (userId) ou objeto { userId, tenantId }
			const tenantId = getTenantIdForWebSocket();

			this.socket.emit("joinUser", {
				userId,
				tenantId,
			});

			sendToTerminal("👤 [SOCKET] Entrou na sala do usuário", {
				userId,
				tenantId,
			});
		}
	}

	/**
	 * Obtém o userId do usuário autenticado
	 */
	private getUserId(): string | null {
		try {
			const userData = TokenService.getUserData();
			const userId = userData?.data?.usuario?.id;
			return userId ? String(userId) : null;
		} catch {
			return null;
		}
	}

	/**
	 * Classifica o tipo de erro de conexão
	 */
	private classifyError(error: Error): string {
		const msg = error.message.toLowerCase();

		if (msg.includes("cors")) return "CORS_BLOCKED";
		if (msg.includes("refused")) return "CONNECTION_REFUSED";
		if (msg.includes("timeout")) return "TIMEOUT";
		if (msg.includes("network")) return "NETWORK_ERROR";
		if (msg.includes("websocket")) return "WEBSOCKET_ERROR";
		if (msg.includes("transport")) return "TRANSPORT_ERROR";
		if (msg.includes("handshake")) return "HANDSHAKE_ERROR";
		if (msg.includes("unauthorized") || msg.includes("401"))
			return "UNAUTHORIZED";
		if (msg.includes("not found") || msg.includes("404")) return "NOT_FOUND";

		return "UNKNOWN";
	}

	/**
	 * Verifica se está conectado
	 */
	isConnected(): boolean {
		return this.socket?.connected ?? false;
	}

	/**
	 * Reaplica token mais recente e força novo handshake.
	 * Usar após login/refresh (evento auth:updated).
	 */
	refreshAuthAndReconnect(
		reason: string = "auth:updated",
		trace: SocketTraceContext = {},
	): void {
		const token = this.getAuthToken();
		const effectiveTrace = {
			source: trace.source || "auth-updated",
			reason: trace.reason || reason,
		};

		if (!token) {
			sendToTerminal(
				"⚠️ [SOCKET] Token ausente após auth update - desconectando socket",
				{ reason },
			);
			this.trace("refreshAuthAndReconnect.missing_token", effectiveTrace);
			this.disconnect(effectiveTrace);
			return;
		}

		if (!this.socket) {
			sendToTerminal("🔄 [SOCKET] Recriando socket após auth update", {
				reason,
			});
			this.trace("refreshAuthAndReconnect.recreate_socket", effectiveTrace);
			this.connect(effectiveTrace);
			return;
		}

		this.applyLatestAuthToSocket();
		sendToTerminal("🔄 [SOCKET] Atualizando token do socket e reconectando", {
			reason,
		});
		this.trace("refreshAuthAndReconnect.reuse_socket", effectiveTrace);

		if (this.socket.connected) {
			this.socket.disconnect();
		}
		this.startHandshake("refreshAuthAndReconnect.socket_connect", effectiveTrace);
		this.socket.connect();
	}

	/**
	 * Desconecta do servidor
	 */
	disconnect(trace: SocketTraceContext = {}): void {
		if (this.socket) {
			this.trace("disconnect", trace, {
				hasSocket: true,
			});
			this.socket.disconnect();
			this.socket = null;
			sendToTerminal("🔌 [SOCKET] Desconectado manualmente");
		}
	}

	/**
	 * Emite evento para o servidor
	 */
	emit(event: string, data: any, trace: SocketTraceContext = {}): void {
		const socket = this.getSocket({
			source: trace.source,
			reason: trace.reason || `emit:${event}`,
		});
		if (!socket) {
			sendToTerminal("⚠️ [SOCKET] Emit ignorado por configuração inválida", {
				event,
				error: this.configurationError,
			});
			this.trace("emit.skipped_no_socket", trace, {
				event,
				error: this.configurationError,
			});
			return;
		}

		if (socket.connected) {
			this.trace("emit.send", trace, {
				event,
				requestId:
					data && typeof data === "object" && "requestId" in data
						? (data as { requestId?: string }).requestId ?? null
						: null,
			});
			socket.emit(event, data);
		} else {
			sendToTerminal("⚠️ [SOCKET] Tentativa de emit sem conexão", { event });
			this.trace("emit.skipped_disconnected", trace, {
				event,
			});
		}
	}

	/**
	 * Registra listener para evento
	 */
	on<T>(
		event: string,
		callback: (data: T) => void,
		trace: SocketTraceContext = {},
	): void {
		const socket = this.getSocket({
			source: trace.source,
			reason: trace.reason || `listen:${event}`,
		});
		if (!socket) {
			return;
		}
		this.trace("listener.on", trace, {
			event,
		});
		socket.on(event, callback);
	}

	/**
	 * Remove listener de evento
	 */
	off(event: string, callback?: (...args: any[]) => void): void {
		if (this.socket) {
			if (callback) {
				this.socket.off(event, callback);
			} else {
				this.socket.off(event);
			}
		}
	}

	/**
	 * Registra listener para um único evento
	 */
	once<T>(
		event: string,
		callback: (data: T) => void,
		trace: SocketTraceContext = {},
	): void {
		const socket = this.getSocket({
			source: trace.source,
			reason: trace.reason || `once:${event}`,
		});
		if (!socket) {
			return;
		}
		this.trace("listener.once", trace, {
			event,
		});
		socket.once(event, callback);
	}

	getDebugSnapshot(): Record<string, unknown> {
		return createSocketTracePayload({
			source: "snapshot",
			action: "debug.snapshot",
			connectionSeq: this.currentConnectionSeq,
			reason: "manual-inspection",
			tenantId: this.safeGetTenantId(),
			connected: this.socket?.connected ?? false,
			socketId: this.socket?.id || "pending",
			hasSocket: !!this.socket,
			isConnecting: this.isConnecting,
			authFailed: this.authFailed,
			reconnectAttempts: this.reconnectAttempts,
		});
	}
}

// Exportar instância singleton
export const socketService = new SocketService();

if (SOCKET_TRACE_ENABLED && typeof window !== "undefined") {
	(
		window as Window & {
			__socketDiagnostics?: {
				createTracePayload: typeof createSocketTracePayload;
				getState: () => Record<string, unknown>;
			};
		}
	).__socketDiagnostics = {
		createTracePayload: createSocketTracePayload,
		getState: () => socketService.getDebugSnapshot(),
	};
}

// Exportar tipos para uso externo
export type { Socket };
